/**
 * Helper function to hide all error messages.
 */
const hideAllErrors = () => {
    const errorMessages = document.querySelectorAll('.js-form-error');
    errorMessages &&
        errorMessages.forEach((errorMessage) => {
            errorMessage.classList.remove('show');
        });
    const validateErrors = document.querySelectorAll('.js-validate.error');
    validateErrors &&
        validateErrors.forEach((el) => {
            el.classList.remove('error');
        });
};

/**
 * Show error for specified HTML element.
 *
 * @param {HTMLElement} element - The HTML element to which the class should be added.
 * @param {string} className - The class name to add to the element.
 */
const showError = (element, className) => {
    const parent = element.closest('.js-form-control');
    const error = parent.querySelector(className);
    error && error.classList.add('show');
};

/**
 * Validates an email address using a regular expression.
 *
 * @param {string} email - The email address to be validated.
 * @return {boolean} True if the email is valid, false otherwise.
 */
const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
};

/**
 * Validates an password using a regular expression.
 *
 * @param {string} pass - The password to be validated.
 * @return {boolean} True if the email is valid, false otherwise.
 */
const validatePwd = (pass) => {
    const emailRegex =
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+{};:,<.>]).{8,}$/;
    return emailRegex.test(pass);
};

/**
 * Displays an error message on a web page by setting the inner HTML of an element
 * with the class 'js-submit-error' and adding the 'show' class to make it visible.
 *
 * @param {string} errorText - The error message to display.
 */
export const showSubmitError = (errorText) => {
    // Get submit error tag
    const submitError = document.querySelector('.js-submit-error');
    if (submitError) {
        submitError.innerHTML = errorText; // Use errorText instead of error
        submitError.classList.add('show');
    }
};

/**
 * Checks if the value of the element with the class 'js-control-phrase' equals '12'.
 *
 * @return {boolean} True if the value is '12', false otherwise.
 */
export const checkControlPhrase = () => {
    // Get the element with class 'js-control-phrase'.
    const element = document.querySelector('.js-control-phrase');

    // Return true if the element exists and its value equals '12', otherwise false.
    return element && element.value === '12';
};

/**
 * Validates form fields and displays error messages for invalid fields.
 *
 * @param {NodeList|HTMLCollection} formControl - A collection of form elements to validate.
 * @return {boolean} - `true` if all fields are valid, `false` if any field is invalid.
 */
const validateFields = (formControl) => {
    let validationStatus = true;
    formControl &&
        formControl.forEach((el) => {
            const validate = el.querySelector('.js-validate');
            if (validate) {
                const tagType = validate?.tagName?.toLowerCase();
                if (tagType === 'input') {
                    const inputType = validate.getAttribute('type');
                    if (
                        inputType === 'email' ||
                        inputType === 'text' ||
                        inputType === 'number' ||
                        inputType === 'password' ||
                        inputType === 'file'
                    ) {
                        const inputValue = validate.value.trim();
                        if (inputType === 'email') {
                            if (!inputValue) {
                                validationStatus = false;
                                showError(el, '.js-empty-field');
                                validate.classList.add('error');
                            } else if (!validateEmail(inputValue)) {
                                validationStatus = false;
                                showError(el, '.js-wrong-email');
                                validate.classList.add('error');
                            }
                        } else if (inputType === 'password') {
                            if (!inputValue) {
                                validationStatus = false;
                                showError(el, '.js-empty-field');
                                validate.classList.add('error');
                            } else if (!validatePwd(inputValue)) {
                                validationStatus = false;
                                showError(el, '.js-pwd-not-valid');
                                validate.classList.add('error');
                            }
                        } else if (inputType === 'text') {
                            if (!inputValue) {
                                validationStatus = false;
                                showError(el, '.js-empty-field');
                                validate.classList.add('error');
                            }
                        } else if (inputType === 'number') {
                            if (!inputValue) {
                                validationStatus = false;
                                showError(el, '.js-empty-field');
                                validate.classList.add('error');
                            } else if (Number.isNaN(inputValue)) {
                                validationStatus = false;
                                showError(el, '.js-wrong-number');
                                validate.classList.add('error');
                            }
                        } else if (
                            inputType === 'file' &&
                            validate.files.length > 0
                        ) {
                            const { files } = validate;
                            // Array of valid file extensions
                            const validExtensions = [
                                'png',
                                'jpg',
                                'jpeg',
                                'pdf',
                            ];
                            // Maximum allowed file size in bytes (5MB)
                            const maxSize = 5 * 1024 * 1024; // 5MB in bytes
                            // Name of the selected file
                            const fileName = files[0].name;
                            // Size of the selected file in bytes
                            const fileSize = files[0].size;
                            // Extension of the selected file (lowercase)
                            const fileExtension = fileName
                                .split('.')
                                .pop()
                                .toLowerCase();

                            // Check file type
                            if (validExtensions.indexOf(fileExtension) === -1) {
                                validationStatus = false;
                                showError(el, '.js-invalid-type');
                            }
                            // Check file size
                            if (fileSize > maxSize) {
                                validationStatus = false;
                                showError(el, '.js-invalid-size');
                            }
                        }
                    } else if (inputType === 'radio') {
                        const checkedRadio = el.querySelector(
                            'input[type="radio"]:checked'
                        ).value;
                        if (!checkedRadio) {
                            validationStatus = false;
                            showError(el, '.js-empty-field');
                            validate.classList.add('error');
                        }
                    }
                } else if (tagType === 'select') {
                    const selectValue = validate.value.trim();
                    if (selectValue === '') {
                        validationStatus = false;
                        showError(el, '.js-empty-field');
                        validate.classList.add('error');
                    }
                } else if (tagType === 'textarea') {
                    const inputValue = validate.value.trim();
                    if (!inputValue) {
                        validationStatus = false;
                        showError(el, '.js-empty-field');
                        validate.classList.add('error');
                    }
                }
            }
        });

    return validationStatus;
};

/**
 * Initialize form validation for a given HTML form element.
 *
 * @param {HTMLFormElement} form - The form element to be validated.
 */
export const initFormValidation = (form) => {
    // Hide all errors before re-validating.
    hideAllErrors();

    const formControl = form.querySelectorAll('.js-form-control');

    return validateFields(formControl);
};

/**
 * Compares two password input fields within a form to check if they match.
 *
 * @param {HTMLFormElement} form - The form element containing the password fields.
 * @return {boolean} - Returns true if both password fields match, otherwise false.
 */
export const comparePwd = (form) => {
    const pass1 = form.querySelector('.js-pass-1');
    const pass2 = form.querySelector('.js-pass-2');
    const compare = pass1.value === pass2.value;
    if (!compare) {
        pass1.classList.add('error');
        pass2.classList.add('error');
        const compareError = document.querySelector('.js-compare-pwd-error');
        compareError && compareError.classList.add('show');
    }

    return compare;
};
